import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ImageIcon, Search, X, Plus, Loader2, ArrowUpDown, Download, ChevronDown } from 'lucide-react';
import ArtworkCard from './ArtworkCard';

const ITEMS_PER_PAGE = 24; // Load 24 artworks at a time

/**
 * ArtworksTab Component
 * Displays a grid of artworks with filtering, sorting, and infinite scroll
 */
const ArtworksTab = ({
  artworks,
  filteredArtworks,
  artworkFilter,
  setArtworkFilter,
  artworkSort,
  setArtworkSort,
  eventDetails,
  isProcessingPayment,
  generatedPaymentLinks,
  copiedPaymentLinks,
  toggleAcceptanceStatus,
  handleGeneratePaymentLink,
  handleCopyPaymentLink,
  generateQRCode,
  handleCheckInArtwork,
  checkInLoading,
  onSelectArtwork,
  onRemoveFromCollection,
  onAddArtwork,
  acceptances = []
}) => {
  const [artworkSearch, setArtworkSearch] = useState('');
  const [generatedPaymentLinksCount, setGeneratedPaymentLinksCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);
  const loaderRef = useRef(null);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Download function
  const handleDownload = (format, filterType) => {
    // Determine which artworks to export based on filterType
    let artworksToExport;
    if (filterType === 'all') {
      artworksToExport = artworks;
    } else if (filterType === 'accepted') {
      artworksToExport = artworks.filter(art => art.accepted === true);
    } else if (filterType === 'not_accepted') {
      artworksToExport = artworks.filter(art => art.accepted === false);
    } else {
      artworksToExport = searchFilteredArtworks;
    }

    // Prepare data rows
    const headers = [
      'Artist Full Name',
      'Artist Nickname',
      'Title',
      'Material',
      'Size',
      'Year',
      'Email',
      'Phone',
      'Accept Terms',
      'Price',
      'Status'
    ];

    const rows = artworksToExport.map(artwork => {
      const artist = artwork.artist || {};
      const fullName = `${artist.first_name || ''} ${artist.last_name || ''}`.trim();
      const size = artwork.width && artwork.height 
        ? `${artwork.width}" × ${artwork.height}"${artwork.depth ? ` × ${artwork.depth}"` : ''}`
        : '';
      
      // Handle year - could be a date string, year number, or year string
      let year = '';
      if (artwork.artwork_completed_date) {
        const dateVal = artwork.artwork_completed_date;
        // If it's already a 4-digit year (number or string)
        if (/^\d{4}$/.test(String(dateVal))) {
          year = String(dateVal);
        } else {
          // Try to parse as date and extract year
          const parsedDate = new Date(dateVal);
          if (!isNaN(parsedDate.getTime())) {
            year = parsedDate.getFullYear();
          }
        }
      }

      // Format price with currency symbol if present
      const price = artwork.price != null ? `$${artwork.price}` : '';
      
      return [
        fullName,
        artist.nickname || '',
        artwork.title || '',
        artwork.description || '',
        size,
        year,
        artist.email || '',
        artist.mobile_phone || '',
        (() => {
          const artistUuid = artwork.artist?.uuid;
          if (!artistUuid) return 'No';
          const acceptanceRecord = acceptances.find(acc => acc.user_id === artistUuid);
          if (!acceptanceRecord) return 'No';
          const termsAccepted = acceptanceRecord.terms_accepted || {};
          return Object.keys(termsAccepted).length > 0 ? 'Yes' : 'No';
        })(),
        price,
        artwork.status || ''
      ];
    });

    if (format === 'csv') {
      // Generate CSV
      const escapeCSV = (val) => {
        const str = String(val || '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `artworks_${filterType}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'excel') {
      // Generate Excel-compatible CSV with BOM for proper Excel encoding
      const escapeCSV = (val) => {
        const str = String(val || '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      // Add BOM for Excel to recognize UTF-8
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `artworks_${filterType}_${new Date().toISOString().split('T')[0]}.xls`;
      link.click();
      URL.revokeObjectURL(url);
    }

    setShowDownloadMenu(false);
  };

  // Filter artworks by search term
  const searchFilteredArtworks = filteredArtworks.filter(artwork => {
    if (!artworkSearch.trim()) return true;
    const s = artworkSearch.toLowerCase();
    const title = (artwork.title || '').toLowerCase();
    const artistName = artwork.artist 
      ? (artwork.artist.nickname || `${artwork.artist.first_name || ''} ${artwork.artist.last_name || ''}`).toLowerCase()
      : (artwork.artist_name || '').toLowerCase();
    return title.includes(s) || artistName.includes(s);
  });

  // Get artworks to display (limited by displayCount)
  const displayedArtworks = searchFilteredArtworks.slice(0, displayCount);
  const hasMore = displayCount < searchFilteredArtworks.length;

  // Reset display count when filters/search change
  useEffect(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, [artworkFilter, artworkSort, artworkSearch]);

  // Load more artworks
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    // Small delay to show loading state
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + ITEMS_PER_PAGE, searchFilteredArtworks.length));
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore, hasMore, searchFilteredArtworks.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, isLoadingMore]);

  return (
    <div className="artworks-tab">
      {/* Artwork Controls Header - Only show if artworks exist */}
      {artworks.length > 0 && (
        <div className="artwork-controls-header" style={{ marginBottom: '1.5rem' }}>
          {/* Search Bar with Sort and Refresh */}
          <div className="search-wrapper" style={{ 
            marginBottom: '1rem',
            display: 'flex', 
            gap: '0.5rem', 
            alignItems: 'center'
          }}>
            <div style={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '0.75rem',
                color: '#9ca3af',
                pointerEvents: 'none'
              }} />
              <input
                type="text"
                placeholder="Search by artwork title or artist name..."
                value={artworkSearch}
                onChange={(e) => setArtworkSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  paddingRight: artworkSearch ? '2.5rem' : '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              {artworkSearch && (
                <button
                  onClick={() => setArtworkSearch('')}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Sort & Filter Dropdown */}
            <div style={{ position: 'relative' }}>
              <select
                value={`${artworkSort}:${artworkFilter}`}
                onChange={(e) => {
                  const [sort, filter] = e.target.value.split(':');
                  if (filter) {
                    setArtworkFilter(filter);
                  }
                  if (sort) {
                    setArtworkSort(sort);
                  }
                }}
                style={{
                  appearance: 'none',
                  padding: '0.75rem 2.25rem 0.75rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  backgroundColor: '#f3f4f6',
                  fontSize: '0.875rem',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: '500',
                  minWidth: '180px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              >
                <optgroup label="Sort By">
                  <option value="date:submitted">Date</option>
                  <option value="title:submitted">Title</option>
                  <option value="artist:submitted">Artist</option>
                  <option value="order:submitted">Order</option>
                </optgroup>
                <optgroup label="Filter">
                  <option value={`${artworkSort}:submitted`}>All ({artworks.length})</option>
                  <option value={`${artworkSort}:accepted`}>Accepted ({artworks.filter(art => art.accepted === true).length})</option>
                  <option value={`${artworkSort}:not_accepted`}>Not Accepted ({artworks.filter(art => art.accepted === false).length})</option>
                </optgroup>
              </select>
              <ArrowUpDown size={14} style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6b7280',
                pointerEvents: 'none'
              }} />
            </div>
          </div>

          {/* Download and Add Artwork Row */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: '1rem'
          }}>
            {/* Download Dropdown */}
            <div style={{ position: 'relative' }} ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              >
              <Download size={16} />
              Download Artworks
              <ChevronDown size={14} style={{ 
                transform: showDownloadMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} />
            </button>

            {showDownloadMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '0.25rem',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 50,
                minWidth: '220px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  padding: '0.5rem 0.75rem', 
                  fontSize: '0.7rem', 
                  fontWeight: '600', 
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  Export as Excel (.xls)
                </div>
                <button
                  onClick={() => handleDownload('excel', 'all')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  All Artworks ({artworks.length})
                </button>
                <button
                  onClick={() => handleDownload('excel', 'accepted')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Accepted ({artworks.filter(art => art.accepted === true).length})
                </button>
                <button
                  onClick={() => handleDownload('excel', 'not_accepted')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Not Accepted ({artworks.filter(art => art.accepted === false).length})
                </button>

                <div style={{ 
                  padding: '0.5rem 0.75rem', 
                  fontSize: '0.7rem', 
                  fontWeight: '600', 
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderTop: '1px solid #e5e7eb',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  Export as CSV
                </div>
                <button
                  onClick={() => handleDownload('csv', 'all')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  All Artworks ({artworks.length})
                </button>
                <button
                  onClick={() => handleDownload('csv', 'accepted')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Accepted ({artworks.filter(art => art.accepted === true).length})
                </button>
                <button
                  onClick={() => handleDownload('csv', 'not_accepted')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    fontSize: '0.875rem',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Not Accepted ({artworks.filter(art => art.accepted === false).length})
                </button>
              </div>
            )}
          </div>

            {onAddArtwork && (
              <button
                onClick={onAddArtwork}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#7c3aed';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#8b5cf6';
                }}
              >
                <Plus size={16} />
                Add Artwork
              </button>
            )}

            {/* Status text summary with counts */}
            <span style={{
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              Showing {searchFilteredArtworks.length} of {artworks.length}
              {artworkFilter !== 'submitted' && (artworkFilter === 'accepted' ? ' accepted' : ' not accepted')}
              {' · '}
              <span style={{ color: '#16a34a', fontWeight: '500' }}>{artworks.filter(art => art.accepted === true).length} accepted</span>
              {' · '}
              <span style={{ color: '#d97706', fontWeight: '500' }}>{artworks.filter(art => art.accepted === false).length} not accepted</span>
            </span>
          </div>
        </div>
      )}

      {searchFilteredArtworks.length === 0 ? (
        <div style={{
          padding: '3rem 1.5rem',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          borderRadius: '0.75rem',
          border: '1px dashed #d1d5db'
        }}>
          <ImageIcon size={48} style={{ 
            color: '#d1d5db',
            marginBottom: '1rem',
            margin: '0 auto 1rem'
          }} />
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 0.5rem 0'
          }}>
            {artworkSearch ? 'No Artworks Found' : 'No Artworks Yet'}
          </h3>
          <p style={{
            fontSize: '0.9rem',
            color: '#6b7280',
            margin: '0 0 1.5rem 0',
            lineHeight: '1.5'
          }}>
            {artworkSearch
              ? `No artworks match "${artworkSearch}"`
              : artworkFilter === 'submitted'
              ? "This collection doesn't contain any submitted artworks yet. Artists will submit their work once the submission form is open."
              : `No ${artworkFilter} artworks found.`
            }
          </p>
          
          {!artworkSearch && artworkFilter === 'submitted' && (
            <button
              onClick={() => {
                // TODO: Open dialog to select artworks from system
                console.log('Open artwork selector');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#7c3aed';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#8b5cf6';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <Plus size={18} />
              Select Artworks from System
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            className="artworks-grid-new"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1.5rem',
              padding: '1rem 0'
            }}
          >
            {displayedArtworks.map((artwork, index) => (
              <ArtworkCard
                key={`artwork-${artwork.vc_id}-${index}`}
                artwork={artwork}
                onSelectArtwork={onSelectArtwork}
              />
            ))}
          </div>

          {/* Infinite scroll loader */}
          {hasMore && (
            <div
              ref={loaderRef}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                gap: '0.75rem'
              }}
            >
              {isLoadingMore ? (
                <>
                  <Loader2 size={24} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading more artworks...</span>
                </>
              ) : (
                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  Showing {displayedArtworks.length} of {searchFilteredArtworks.length} artworks
                </span>
              )}
            </div>
          )}

          {/* End of list indicator */}
          {!hasMore && searchFilteredArtworks.length > ITEMS_PER_PAGE && (
            <div style={{
              textAlign: 'center',
              padding: '1.5rem',
              color: '#9ca3af',
              fontSize: '0.875rem'
            }}>
              All {searchFilteredArtworks.length} artworks loaded
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArtworksTab;
